/**
 * Utility function to validate email structure
 * @param {string} email
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
    // Simple regex for email structure: start of string, non-whitespace chars, @, non-whitespace chars, ., non-whitespace chars, end of string.
    const emailRegex = /^\S+@\S+\.\S+$/;
    return emailRegex.test(email);
};

/**
 * Utility function to validate DOB is between 18 and 100 years old
 * @param {string} dobString - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
export const isValidDOB = (dobString) => {
    if (!dobString) return false;

    const dobDate = new Date(dobString);
    const now = new Date();

    // 1. Calculate the date 18 years ago (Must be born on or before this date)
    const eighteenYearsAgo = new Date(
        now.getFullYear() - 18, 
        now.getMonth(), 
        now.getDate()
    );

    // 2. Calculate the date 100 years ago (Must be born after this date)
    const oneHundredYearsAgo = new Date(
        now.getFullYear() - 100, 
        now.getMonth(), 
        now.getDate()
    );

    // Must be 18 or older (DOB <= 18 years ago) AND younger than 100 (DOB > 100 years ago)
    return dobDate <= eighteenYearsAgo && dobDate > oneHundredYearsAgo;
};

/* ===========================
   NEW VALIDATORS FOR PAYMENTS
   =========================== */

/** not blank (string with at least one non-space char) */
export const isNotBlank = (s) => typeof s === "string" && s.trim().length > 0;

/** integer-only (no decimals, no signs) */
export const isInteger = (s) => /^[0-9]+$/.test(String(s || ""));

/** E.164 phone (e.g., +447911123456) */
export const isE164Phone = (s) => /^\+[1-9]\d{6,14}$/.test(String(s || ""));

/** Luhn checksum for credit cards */
export const luhnCheck = (pan) => {
  if (!pan) return false;
  const s = String(pan).replace(/\s+/g, "");
  if (!/^\d{12,19}$/.test(s)) return false;
  let sum = 0;
  let dbl = false;
  for (let i = s.length - 1; i >= 0; i--) {
    let d = parseInt(s.charAt(i), 10);
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
};

/** expiry valid: month 1..12, and not in the past (YY, not YYYY) */
export const isValidExpiry = (mm, yy) => {
  const m = parseInt(mm, 10);
  const y = parseInt(yy, 10);
  if (!Number.isInteger(m) || !Number.isInteger(y)) return false;
  if (m < 1 || m > 12) return false;

  // interpret yy as 20yy
  const fullYear = 2000 + y;
  const now = new Date();
  // end of expiry month
  const exp = new Date(fullYear, m, 0, 23, 59, 59, 999);
  return exp >= now;
};

/** CVC valid: 3 digits (most) or 4 digits (AmEx) */
export const isValidCVC = (cvc, brand = "generic") => {
  const s = String(cvc || "").trim();
  if (brand.toLowerCase() === "amex") return /^\d{4}$/.test(s);
  return /^\d{3}$/.test(s);
};

/* ===========================
   MEMBERSHIP FLOW VALIDATORS
   =========================== */

/**
 * Checks if the final attestation document has been completed and saved.
 * Relies on the 'theirDocs' key set by DeclarationsInteractive.jsx.
 * @returns {boolean}
 */
export const isAttestationComplete = () => {
    try {
        const STORAGE_KEY_DOCS = 'theirDocs'; 
        // If the item exists (even as an empty string, though it should be JSON), it's considered complete
        return !!localStorage.getItem(STORAGE_KEY_DOCS);
    } catch {
        return false;
    }
};